import { useCallback, useEffect, useMemo, useState } from "react";
import {
	ColumnDef,
	RowSelectionState,
	SortingState,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { BellRing, History, PauseCircle, PlayCircle, Send } from "lucide-react";
import { EmptyDataPlaceholder } from "@/components/src/empty-with-label/empty-data-placeholder.component";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuTrigger,
} from "../../../../components/ui/context-menu";
import {
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableRow,
} from "../../../../components/ui/table";
import { cn } from "../../../../lib/utils";
import { useCentralStore } from "../../../../store/Central";
import { useRemindersStore } from "../../../../store/Reminders";
import {
	MenuItemData,
	SharedMenuItem,
} from "../../components/shared-menu-item.component";
import { useInvoiceReminderActions } from "../hooks/useInvoiceReminderActions";
import { formatCurrency, formatDate } from "../../components/table-columns";
import {
	MahnwesenFilters,
	MahnwesenView,
	PaymentReminderCandidate,
	ReminderCandidateMode,
} from "../payment-reminder.types";
import { filterReminderCandidates } from "../utils/filter-reminder-candidates";
import { getReminderActionLabel } from "../utils/reminder-stage";
import { CreateRemindersDialog } from "./create-reminders-dialog.component";
import { MahnwesenToolbar } from "./mahnwesen-toolbar.component";
import { PauseRemindersDialog } from "./pause-reminders-dialog.component";
import { SortableTableHead } from "./sortable-table-head.component";

const EMPTY_LABELS: Record<ReminderCandidateMode, string> = {
	due: "Keine fälligen Mahnungen",
	collection: "Keine Inkassofälle",
	paused: "Keine pausierten Rechnungen",
};

const INITIAL_FILTERS: MahnwesenFilters = {
	search: "",
	recipientId: null,
	stage: null,
};

const RIGHT_ALIGNED_COLUMNS = new Set(["open_amount", "fee", "days_overdue"]);

interface RemindersDueTabProps {
	mode: ReminderCandidateMode;
	onViewChange: (view: MahnwesenView) => void;
}

export const RemindersDueTab: React.FC<RemindersDueTabProps> = ({
	mode,
	onViewChange,
}) => {
	const candidates = useRemindersStore((state) => state.candidates);
	const loading = useRemindersStore((state) => state.candidatesLoading);
	const fetchCandidates = useRemindersStore((state) => state.fetchCandidates);
	const resumeReminders = useRemindersStore((state) => state.resumeReminders);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [sorting, setSorting] = useState<SortingState>([]);
	const [dialog, setDialog] = useState<"create" | "pause" | null>(null);
	const [filters, setFilters] = useState<MahnwesenFilters>(INITIAL_FILTERS);
	const maxStage = useCentralStore(
		(state) => state.organization?.mahnung_max_count ?? 1
	);

	const handleRefresh = useCallback(() => {
		setRowSelection({});
		fetchCandidates(mode);
	}, [fetchCandidates, mode]);

	const { reminderActions, reminderDialogs } =
		useInvoiceReminderActions(handleRefresh);

	const rowMenuItems = (
		candidate: PaymentReminderCandidate
	): MenuItemData[] => {
		const invoice = {
			invoiceId: candidate.invoice_id,
			docNumber: candidate.doc_number,
			paused: candidate.is_paused,
			pausedUntil: candidate.paused_until || null,
			stopReason: candidate.stop_reason || null,
		};
		return [
			{
				icon: <BellRing className="h-4 w-4 flex-shrink-0" />,
				label: getReminderActionLabel(candidate.next_stage),
				onClick: () => reminderActions.onCreateReminder(invoice),
				disabled:
					candidate.is_paused || candidate.next_stage > maxStage,
				testId: "candidate-menu-create-reminder",
			},
			candidate.is_paused
				? {
						icon: <PlayCircle className="h-4 w-4 flex-shrink-0" />,
						label: "Mahnstopp aufheben",
						onClick: () =>
							reminderActions.onResumeReminders(invoice),
						testId: "candidate-menu-resume-reminders",
					}
				: {
						icon: <PauseCircle className="h-4 w-4 flex-shrink-0" />,
						label: "Mahnstopp setzen...",
						onClick: () =>
							reminderActions.onPauseReminders(invoice),
						testId: "candidate-menu-pause-reminders",
					},
			{
				icon: <History className="h-4 w-4 flex-shrink-0" />,
				label: "Mahnverlauf anzeigen",
				onClick: () => reminderActions.onShowReminderHistory(invoice),
				disabled: candidate.current_stage === 0 && !candidate.is_paused,
				testId: "candidate-menu-reminder-history",
			},
		];
	};

	const filteredCandidates = useMemo(
		() => filterReminderCandidates(candidates, filters, mode),
		[candidates, filters, mode]
	);

	useEffect(() => {
		fetchCandidates(mode);
	}, [fetchCandidates, mode]);

	const columns = useMemo<ColumnDef<PaymentReminderCandidate>[]>(
		() => [
			{
				id: "select",
				enableSorting: false,
				header: ({ table }) => (
					<Checkbox
						checked={
							table.getIsAllRowsSelected()
								? true
								: table.getIsSomeRowsSelected()
									? "indeterminate"
									: false
						}
						onCheckedChange={(checked) =>
							table.toggleAllRowsSelected(checked === true)
						}
						aria-label="Alle Rechnungen auswählen"
						data-testid="reminder-select-all-checkbox"
					/>
				),
				cell: ({ row }) => (
					<Checkbox
						checked={row.getIsSelected()}
						onCheckedChange={(checked) =>
							row.toggleSelected(checked === true)
						}
						aria-label={`Rechnung ${row.original.doc_number} auswählen`}
						data-testid={`reminder-select-${row.original.doc_number}`}
					/>
				),
			},
			{
				accessorKey: "doc_number",
				header: "Rechnungsnummer",
				cell: ({ getValue }) => (
					<span className="font-medium">{getValue<string>()}</span>
				),
			},
			{
				accessorKey: "recipient_name",
				header: "Empfänger",
			},
			{
				accessorKey: "invoice_date",
				header: "Rechnungsdatum",
				cell: ({ getValue }) => formatDate(getValue<string>()),
			},
			{
				accessorKey: "days_overdue",
				header: "Fällig seit",
				cell: ({ getValue }) => {
					const days = getValue<number>();
					return days > 0 ? (
						<span className="font-mono">{days} Tage</span>
					) : (
						<span className="text-gray-400">–</span>
					);
				},
			},
			{
				accessorKey: "open_amount",
				header: "Offen",
				cell: ({ getValue }) => (
					<span className="font-mono font-semibold text-red-600">
						{formatCurrency(getValue<number>())}
					</span>
				),
			},
			{
				accessorKey: "currency",
				header: "Währung",
			},
			{
				accessorKey: "current_stage",
				header: "Mahnstufe",
				cell: ({ row }) => (
					<span className="whitespace-nowrap">
						{getReminderStageLabel(row.original.current_stage)}
						{mode === "due" && (
							<span className="text-gray-500">
								{" → "}
								{getReminderStageLabel(row.original.next_stage)}
							</span>
						)}
						{mode === "collection" && row.original.is_paused && (
							<span className="text-gray-500"> · pausiert</span>
						)}
					</span>
				),
			},
			mode === "paused"
				? {
						id: "pause",
						header: "Mahnstopp",
						enableSorting: false,
						cell: ({ row }) => (
							<span className="whitespace-nowrap">
								{row.original.paused_until
									? `bis ${formatDate(
											row.original.paused_until
										)}`
									: "unbefristet"}
								{row.original.stop_reason && (
									<span className="text-gray-500">
										{" · "}
										{row.original.stop_reason}
									</span>
								)}
							</span>
						),
					}
				: {
						id: "fee",
						accessorFn: (candidate) =>
							mode === "due"
								? candidate.next_stage_fee
								: candidate.open_fee_amount,
						header: mode === "due" ? "Gebühr" : "Offene Gebühren",
						cell: ({ getValue }) => (
							<span className="font-mono">
								{formatCurrency(getValue<number>())}
							</span>
						),
					},
		],
		[mode]
	);

	const table = useReactTable({
		data: filteredCandidates,
		columns,
		state: { rowSelection, sorting },
		getRowId: (row) => String(row.invoice_id),
		onRowSelectionChange: setRowSelection,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});

	const selected = table
		.getSelectedRowModel()
		.rows.map((row) => row.original);

	const handleResume = async () => {
		await resumeReminders(
			selected.map((candidate) => candidate.invoice_id)
		);
		handleRefresh();
	};

	const bulkActions = selected.length > 0 && (
		<div
			className="flex items-center gap-2"
			data-testid="reminder-bulk-bar"
		>
			<span className="whitespace-nowrap text-sm text-gray-500">
				{selected.length} ausgewählt
			</span>
			{mode === "due" && (
				<Button
					size="sm"
					onClick={() => setDialog("create")}
					aria-label="Mahnungen erstellen"
					data-testid="create-reminders-button"
				>
					<Send />
					Mahnungen erstellen
				</Button>
			)}
			{mode === "paused" ? (
				<Button
					size="sm"
					variant="outline"
					onClick={handleResume}
					aria-label="Mahnstopp aufheben"
					data-testid="resume-reminders-button"
				>
					<PlayCircle />
					Mahnstopp aufheben
				</Button>
			) : (
				<Button
					size="sm"
					variant="outline"
					onClick={() => setDialog("pause")}
					aria-label="Mahnstopp setzen"
					data-testid="pause-reminders-button"
				>
					<PauseCircle />
					Mahnstopp setzen
				</Button>
			)}
		</div>
	);

	const rows = table.getRowModel().rows;

	return (
		<>
			<MahnwesenToolbar
				view={mode}
				onViewChange={onViewChange}
				filters={filters}
				onFiltersChange={(changes) =>
					setFilters((current) => ({ ...current, ...changes }))
				}
				actions={bulkActions}
			/>

			{loading && candidates.length === 0 ? (
				<div className="flex items-center justify-center py-8 text-sm text-gray-500">
					Lade Rechnungen…
				</div>
			) : rows.length === 0 ? (
				<EmptyDataPlaceholder
					label={
						candidates.length > 0
							? "Keine Rechnungen für diese Filter"
							: EMPTY_LABELS[mode]
					}
				/>
			) : (
				<div className="overflow-x-auto">
					<Table data-testid="reminder-candidates-table">
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<SortableTableHead
											key={header.id}
											sorted={header.column.getIsSorted()}
											onToggle={
												header.column.getCanSort()
													? header.column.getToggleSortingHandler()
													: undefined
											}
											alignRight={RIGHT_ALIGNED_COLUMNS.has(
												header.column.id
											)}
										>
											{flexRender(
												header.column.columnDef.header,
												header.getContext()
											)}
										</SortableTableHead>
									))}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{rows.map((row) => (
								<ContextMenu key={row.id}>
									<ContextMenuTrigger asChild>
										<TableRow
											data-state={
												row.getIsSelected()
													? "selected"
													: undefined
											}
											data-testid={`reminder-candidate-row-${row.original.doc_number}`}
										>
											{row
												.getVisibleCells()
												.map((cell) => (
													<TableCell
														key={cell.id}
														className={cn(
															"px-2 py-1 text-sm",
															RIGHT_ALIGNED_COLUMNS.has(
																cell.column.id
															) && "text-right"
														)}
													>
														{flexRender(
															cell.column
																.columnDef.cell,
															cell.getContext()
														)}
													</TableCell>
												))}
										</TableRow>
									</ContextMenuTrigger>
									<ContextMenuContent>
										{rowMenuItems(row.original).map(
											(item) => (
												<SharedMenuItem
													key={item.testId}
													item={item}
													asContextMenu
												/>
											)
										)}
									</ContextMenuContent>
								</ContextMenu>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			<CreateRemindersDialog
				open={dialog === "create"}
				candidates={selected}
				ignoreSchedule={false}
				onClose={() => setDialog(null)}
				onCompleted={handleRefresh}
			/>
			<PauseRemindersDialog
				open={dialog === "pause"}
				invoiceIds={selected.map((candidate) => candidate.invoice_id)}
				onClose={() => setDialog(null)}
				onCompleted={handleRefresh}
			/>
			{reminderDialogs}
		</>
	);
};
